/**
 * ClientOnboardingReview Component
 * Final review of all onboarding information before submission
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OnboardingProgressBar } from '../../components/ui';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboardingReview.module.css';

const ClientOnboardingReview = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [accountType, setAccountType] = useState('');
    const [businessInfo, setBusinessInfo] = useState(null);
    const [contacts, setContacts] = useState(null);
    const [reportingRequirements, setReportingRequirements] = useState(null);
    const [qcDisposal, setQcDisposal] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [signatureName, setSignatureName] = useState('');
    const [signatureDate, setSignatureDate] = useState('');
    const [signatureError, setSignatureError] = useState('');

    useEffect(() => {
        // Load all saved data from session storage
        const savedAccountType = sessionStorage.getItem(`onboarding_account_type_${token}`);
        const savedBusinessInfo = sessionStorage.getItem(`onboarding_business_info_${token}`);
        const savedContacts = sessionStorage.getItem(`onboarding_contacts_${token}`);
        const savedReporting = sessionStorage.getItem(`onboarding_reporting_requirements_${token}`);
        const savedQcDisposal = sessionStorage.getItem(`onboarding_qc_disposal_${token}`);

        if (savedAccountType) setAccountType(savedAccountType);
        if (savedBusinessInfo) setBusinessInfo(JSON.parse(savedBusinessInfo));
        if (savedContacts) setContacts(JSON.parse(savedContacts));
        if (savedReporting) setReportingRequirements(JSON.parse(savedReporting));
        if (savedQcDisposal) setQcDisposal(JSON.parse(savedQcDisposal));
        
        // Set today's date as default
        const today = new Date().toISOString().split('T')[0];
        setSignatureDate(today);
    }, [token]);

    const handleEdit = (section) => {
        navigate(`/client-onboarding/${token}/${section}`);
    };

    const handleSubmit = async () => {
        // Validate signature
        if (!signatureName.trim()) {
            setSignatureError('Please enter your full name to certify this information.');
            return;
        }
        
        if (!signatureDate) {
            setSignatureError('Please select a date.');
            return;
        }
        
        setSignatureError('');
        setIsSubmitting(true);
        
        try {
            // Compile all data for final submission
            const finalStepData = {
                signature: {
                    name: signatureName,
                    date: signatureDate
                }
            };

            // Gather all step data from sessionStorage to send with completion
            // This ensures all data is included even if some steps weren't properly saved to backend
            const allStepData = {};
            
            // Step 1: Terms & Conditions Agreement
            const termsAgreed = sessionStorage.getItem(`onboarding_terms_agreed_${token}`);
            if (termsAgreed === 'true') {
                allStepData['1'] = { termsAgreed: true };
            }
            
            // Step 2: Account Type
            if (accountType) {
                allStepData['2'] = { accountType };
            }
            
            // Step 3: Business Info (only if business account)
            if (businessInfo && accountType === 'business') {
                allStepData['3'] = businessInfo;
            }
            
            // Step 4: Billing & Reporting Contacts
            if (contacts) {
                allStepData['4'] = contacts;
            }
            
            // Step 5: Reporting Requirements
            if (reportingRequirements) {
                allStepData['5'] = reportingRequirements;
            }
            
            // Step 6: QC & Disposal
            if (qcDisposal) {
                allStepData['6'] = qcDisposal;
            }
            
            // Step 8: Final signature
            allStepData['8'] = finalStepData;

            console.log('Submitting complete form with all step data:', allStepData);

            // Complete the multi-step form (step 8: Review & Submit)
            // Include allStepData to ensure backend has complete picture
            const result = await publicTokenService.completeForm(token, {
                finalStepData,
                allStepData  // Send all step data to ensure nothing is missing
            });

            if (result.success) {
                console.log('Onboarding completed successfully:', result);
                
                // Clear local session storage after successful submission
                sessionStorage.removeItem(`onboarding_account_type_${token}`);
                sessionStorage.removeItem(`onboarding_business_info_${token}`);
                sessionStorage.removeItem(`onboarding_contacts_${token}`);
                sessionStorage.removeItem(`onboarding_reporting_requirements_${token}`);
                sessionStorage.removeItem(`onboarding_qc_disposal_${token}`);
                sessionStorage.removeItem(`onboarding_terms_agreed_${token}`);

                // Navigate to success page
                navigate(`/client-onboarding/${token}/success`);
            } else {
                throw new Error(result.message || 'Failed to submit onboarding');
            }
        } catch (error) {
            console.error('Error submitting onboarding:', error);
            setSignatureError(error.message || 'There was an error submitting your information. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoBack = () => {
        navigate(`/client-onboarding/${token}/qc-disposal`);
    };

    const formatBoolean = (value) => value ? 'Yes' : 'No';

    const getDeliveryMethods = (delivery) => {
        const methods = [];
        if (delivery.faxed) methods.push('Faxed');
        if (delivery.emailed) methods.push('Emailed');
        if (delivery.mailedOnly) methods.push('Mailed Only');
        return methods.length > 0 ? methods.join(', ') : 'None selected';
    };

    const getRegulatoryMethods = (methods) => {
        const selected = [];
        if (methods.fax) selected.push('Fax');
        if (methods.email) selected.push('Email');
        if (methods.writeOnForms) selected.push('CA Write on Forms');
        return selected.length > 0 ? selected.join(', ') : 'None selected';
    };

    const getTATMethods = (tat) => {
        const methods = [];
        if (tat.normal) methods.push('Normal');
        if (tat.fiveDay) methods.push('5 Day');
        if (tat.threeDay) methods.push('3 Day');
        if (tat.fortyEightHour) methods.push('48 Hour');
        if (tat.twentyFourHour) methods.push('24 Hour');
        if (tat.other) methods.push(`Other: ${tat.otherText}`);
        return methods.length > 0 ? methods.join(', ') : 'None selected';
    };

    const getElectronicFormats = (formats) => {
        const selected = [];
        if (formats.pdf) selected.push('PDF');
        if (formats.excelFile) selected.push('Excel File');
        if (formats.csvFile) selected.push('CSV File');
        if (formats.other) selected.push(`Other: ${formats.otherText}`);
        return selected.length > 0 ? selected.join(', ') : 'None selected';
    };

    const getClientTypes = (clientType) => {
        const types = [];
        if (clientType.consultant) types.push('Consultant');
        if (clientType.industry) types.push('Industry');
        if (clientType.private) types.push('Private');
        if (clientType.municipality) types.push('Municipality');
        if (clientType.other) types.push(`Other: ${clientType.otherText}`);
        return types.length > 0 ? types.join(', ') : 'None selected';
    };

    const getRegulations = (regs) => {
        const regulations = [];
        if (regs.swda) regulations.push('SWDA');
        if (regs.cwa) regulations.push('CWA');
        if (regs.npdes) regulations.push('NPDES');
        if (regs.rcra) regulations.push('RCRA');
        if (regs.psCode) regulations.push(`PS Code: ${regs.psCode}`);
        return regulations.length > 0 ? regulations.join(', ') : 'None selected';
    };

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.logoHeader}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoHeaderImg}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.formContainer}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>Review Your Information</h1>
                            <p className={styles.subtitle}>
                                Please review all information before submitting
                            </p>
                        </div>

                        <div className={styles.reviewSections}>
                            {/* Account Type */}
                            <div className={styles.section}>
                                <div className={styles.sectionHeader}>
                                    <h2 className={styles.sectionTitle}>Account Type</h2>
                                    <button
                                        type="button"
                                        onClick={() => handleEdit('account-type')}
                                        className={styles.editButton}
                                    >
                                        Edit
                                    </button>
                                </div>
                                <div className={styles.sectionContent}>
                                    <div className={styles.dataRow}>
                                        <span className={styles.dataLabel}>Account Type:</span>
                                        <span className={styles.dataValue}>
                                            {accountType === 'business' ? 'Business Entity' : 'Individual/Homeowner'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Business Information */}
                            {accountType === 'business' && businessInfo && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}>
                                        <h2 className={styles.sectionTitle}>Business Information</h2>
                                        <button
                                            type="button"
                                            onClick={() => handleEdit('business-info')}
                                            className={styles.editButton}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Business Name:</span>
                                            <span className={styles.dataValue}>{businessInfo.businessName}</span>
                                        </div>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Address:</span>
                                            <span className={styles.dataValue}>
                                                {businessInfo.address}
                                                {businessInfo.address2 && `, ${businessInfo.address2}`}
                                                <br />
                                                {businessInfo.city}, {businessInfo.state} {businessInfo.zipCode}
                                            </span>
                                        </div>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Client Type:</span>
                                            <span className={styles.dataValue}>{getClientTypes(businessInfo.clientType)}</span>
                                        </div>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Applicable Regulations:</span>
                                            <span className={styles.dataValue}>{getRegulations(businessInfo.applicableRegulations)}</span>
                                        </div>
                                        {businessInfo.additionalDocuments && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Additional Documents:</span>
                                                <span className={styles.dataValue}>{businessInfo.additionalDocuments}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Contacts */}
                            {contacts && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}>
                                        <h2 className={styles.sectionTitle}>Contact Information</h2>
                                        <button
                                            type="button"
                                            onClick={() => handleEdit('billing-reporting')}
                                            className={styles.editButton}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        {/* Billing Contact */}
                                        <div className={styles.contactBlock}>
                                            <h3 className={styles.contactTitle}>Billing Contact</h3>
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Name:</span>
                                                <span className={styles.dataValue}>
                                                    {contacts.billingContact.firstName} {contacts.billingContact.lastName}
                                                </span>
                                            </div>
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Email:</span>
                                                <span className={styles.dataValue}>{contacts.billingContact.email}</span>
                                            </div>
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Phone:</span>
                                                <span className={styles.dataValue}>{contacts.billingContact.phone}</span>
                                            </div>
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Address:</span>
                                                <span className={styles.dataValue}>
                                                    {contacts.billingContact.address1}
                                                    {contacts.billingContact.address2 && `, ${contacts.billingContact.address2}`}
                                                    <br />
                                                    {contacts.billingContact.city}, {contacts.billingContact.state} {contacts.billingContact.zipCode}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Reporting Contact */}
                                        <div className={styles.contactBlock}>
                                            <h3 className={styles.contactTitle}>Reporting Contact</h3>
                                            {contacts.sameAsBilling ? (
                                                <p className={styles.sameAsText}>Same as Billing Contact</p>
                                            ) : (
                                                <>
                                                    <div className={styles.dataRow}>
                                                        <span className={styles.dataLabel}>Name:</span>
                                                        <span className={styles.dataValue}>
                                                            {contacts.reportingContact.firstName} {contacts.reportingContact.lastName}
                                                        </span>
                                                    </div>
                                                    <div className={styles.dataRow}>
                                                        <span className={styles.dataLabel}>Email:</span>
                                                        <span className={styles.dataValue}>{contacts.reportingContact.email}</span>
                                                    </div>
                                                    <div className={styles.dataRow}>
                                                        <span className={styles.dataLabel}>Phone:</span>
                                                        <span className={styles.dataValue}>{contacts.reportingContact.phone}</span>
                                                    </div>
                                                    <div className={styles.dataRow}>
                                                        <span className={styles.dataLabel}>Address:</span>
                                                        <span className={styles.dataValue}>
                                                            {contacts.reportingContact.address1}
                                                            {contacts.reportingContact.address2 && `, ${contacts.reportingContact.address2}`}
                                                            <br />
                                                            {contacts.reportingContact.city}, {contacts.reportingContact.state} {contacts.reportingContact.zipCode}
                                                        </span>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Additional Contacts */}
                                        {contacts.additional && contacts.additional.length > 0 && (
                                            <div className={styles.contactBlock}>
                                                <h3 className={styles.contactTitle}>Additional Contacts ({contacts.additionalContacts.length})</h3>
                                                {contacts.additionalContacts.map((contact, index) => (
                                                    <div key={contact.id} className={styles.additionalContact}>
                                                        <p className={styles.contactNumber}>Contact {index + 1}</p>
                                                        <div className={styles.dataRow}>
                                                            <span className={styles.dataLabel}>Type:</span>
                                                            <span className={styles.dataValue}>
                                                                {[contact.isBilling && 'Billing', contact.isReporting && 'Reporting']
                                                                    .filter(Boolean)
                                                                    .join(', ')}
                                                            </span>
                                                        </div>
                                                        <div className={styles.dataRow}>
                                                            <span className={styles.dataLabel}>Name:</span>
                                                            <span className={styles.dataValue}>
                                                                {contact.firstName} {contact.lastName}
                                                            </span>
                                                        </div>
                                                        <div className={styles.dataRow}>
                                                            <span className={styles.dataLabel}>Email:</span>
                                                            <span className={styles.dataValue}>{contact.email}</span>
                                                        </div>
                                                        <div className={styles.dataRow}>
                                                            <span className={styles.dataLabel}>Phone:</span>
                                                            <span className={styles.dataValue}>{contact.phone}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Reporting Requirements */}
                            {reportingRequirements && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}>
                                        <h2 className={styles.sectionTitle}>Reporting Requirements</h2>
                                        <button
                                            type="button"
                                            onClick={() => handleEdit('reporting-requirements')}
                                            className={styles.editButton}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Regulatory Submission Required:</span>
                                            <span className={styles.dataValue}>
                                                {formatBoolean(reportingRequirements.regulatorySubmission.required)}
                                            </span>
                                        </div>
                                        {reportingRequirements.regulatorySubmission.required && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Submission Method:</span>
                                                <span className={styles.dataValue}>
                                                    {getRegulatoryMethods(reportingRequirements.regulatorySubmission.method)}
                                                </span>
                                            </div>
                                        )}
                                        {reportingRequirements.specialInstructions && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Special Instructions:</span>
                                                <span className={styles.dataValue}>{reportingRequirements.specialInstructions}</span>
                                            </div>
                                        )}
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Electronic Format Needed:</span>
                                            <span className={styles.dataValue}>
                                                {formatBoolean(reportingRequirements.electronicFormat.needed)}
                                            </span>
                                        </div>
                                        {reportingRequirements.electronicFormat.needed && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Electronic Formats:</span>
                                                <span className={styles.dataValue}>
                                                    {getElectronicFormats(reportingRequirements.electronicFormat.formats)}
                                                </span>
                                            </div>
                                        )}
                                        {reportingRequirements.formattingRequirement && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Formatting Requirement:</span>
                                                <span className={styles.dataValue}>{reportingRequirements.formattingRequirement}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* QA/QC Requirements & Sample Disposal Section */}
                            {qcDisposal && (
                                <div className={styles.section}>
                                    <div className={styles.sectionHeader}>
                                        <h3 className={styles.sectionTitle}>QA/QC Requirements & Sample Disposal</h3>
                                        <button
                                            type="button"
                                            onClick={() => handleEdit('qc-disposal')}
                                            className={styles.editButton}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                    <div className={styles.sectionContent}>
                                        {qcDisposal.projectName && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Project Name/Number:</span>
                                                <span className={styles.dataValue}>{qcDisposal.projectName}</span>
                                            </div>
                                        )}
                                        
                                        <h4 className={styles.subsectionTitle}>QA/QC Requirements</h4>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>QC Data Reported:</span>
                                            <span className={styles.dataValue}>{formatBoolean(qcDisposal.qcDataReported)}</span>
                                        </div>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Standard Low Level Limits:</span>
                                            <span className={styles.dataValue}>{formatBoolean(qcDisposal.standardLowLevelLimits)}</span>
                                        </div>
                                        {qcDisposal.specialReportingLimits && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Special Reporting Limits:</span>
                                                <span className={styles.dataValue}>{qcDisposal.specialReportingLimits}</span>
                                            </div>
                                        )}
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Internal Chain of Custody:</span>
                                            <span className={styles.dataValue}>{formatBoolean(qcDisposal.internalChainOfCustody)}</span>
                                        </div>
                                        {qcDisposal.chainOfCustodyInstructions && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Special Instructions:</span>
                                                <span className={styles.dataValue}>{qcDisposal.chainOfCustodyInstructions}</span>
                                            </div>
                                        )}
                                        
                                        <h4 className={styles.subsectionTitle}>Sample Disposal</h4>
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Return Samples to Client:</span>
                                            <span className={styles.dataValue}>{formatBoolean(qcDisposal.returnSamples)}</span>
                                        </div>
                                        {qcDisposal.returnSamples && qcDisposal.returnSamplesAfter && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Return After:</span>
                                                <span className={styles.dataValue}>{qcDisposal.returnSamplesAfter}</span>
                                            </div>
                                        )}
                                        <div className={styles.dataRow}>
                                            <span className={styles.dataLabel}>Hold Until Notified:</span>
                                            <span className={styles.dataValue}>{formatBoolean(qcDisposal.holdUntilNotified)}</span>
                                        </div>
                                        {qcDisposal.holdUntilNotified && qcDisposal.holdUntilNotifiedAfter && (
                                            <div className={styles.dataRow}>
                                                <span className={styles.dataLabel}>Hold Until After:</span>
                                                <span className={styles.dataValue}>{qcDisposal.holdUntilNotifiedAfter}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Signature Section */}
                        <div className={styles.signatureSection}>
                            <div className={styles.signatureHeader}>
                                <h2 className={styles.signatureTitle}>Certification and Signature</h2>
                            </div>
                            <div className={styles.signatureContent}>
                                <div className={styles.certificationBox}>
                                    <p className={styles.certificationText}>
                                        I certify that the information provided in this onboarding application is true and accurate to the best of my knowledge. 
                                        I understand that this information will be used to establish our business relationship and service agreement.
                                    </p>
                                </div>
                                
                                <div className={styles.signatureFields}>
                                    <div className={styles.signatureField}>
                                        <label className={styles.signatureLabel}>
                                            Full Name <span className={styles.required}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={signatureName}
                                            onChange={(e) => setSignatureName(e.target.value)}
                                            placeholder="Type your full name"
                                            className={styles.signatureInput}
                                        />
                                    </div>
                                    
                                    <div className={styles.signatureField}>
                                        <label className={styles.signatureLabel}>
                                            Date <span className={styles.required}>*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={signatureDate}
                                            onChange={(e) => setSignatureDate(e.target.value)}
                                            className={styles.signatureInput}
                                        />
                                    </div>
                                </div>
                                
                                {signatureError && (
                                    <div className={styles.errorMessage}>
                                        {signatureError}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.actions}>
                            <button
                                type="button"
                                onClick={handleGoBack}
                                className={styles.backButton}
                                disabled={isSubmitting}
                            >
                                ← Go Back
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className={styles.submitButton}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit Client Onboarding →'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <p className={styles.footerText}>
                    Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                </p>
            </div>
        </React.Fragment>
    );
};

export default ClientOnboardingReview;
